import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import prefixProperties from '~/prix/utils/prefixProperties'
import { pick } from '~/prix/utils/types/object'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import { eadAttendanceVariable } from '~/packages/ead/stats/eadAttendanceStats.definition'
import distinctLegalEntitiesEmissorNfeStatsAction from './distinctLegalEntitiesEmissorNfeStats.action.json'

const emissorDistinctLegalEntityShape = {
  id: string().label('ID', 'Identificador', 'Código'),
  cnpj: string().isOptional().label('CNPJ da empresa'),
  attendanceDate: dateTime().isOptional().label('Data do atendimento'),
  createdAt: dateTime().isOptional().label('Data de criação'),
  updatedAt: dateTime().isOptional().label('Data de atualização'),
}

const combinedShape = object({
  ...prefixProperties('emissorDistinctLegalEntity.', emissorDistinctLegalEntityShape),

  id: string().label('ID', 'Identificador', 'Código'),
  tradeName: string().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  cnpj: string().label('CNPJ'),
  openingDate: dateTime().label('Data de abertura'),
  mainCnaeId: string().label('CNAE principal'),
  mainCnaeDescription: string().label('Descrição do CNAE principal'),
  cnaes: string().label('CNAEs secundários'),
  legalNatureId: string().label('ID da natureza jurídica'),
  legalNatureDescription: string().label('Descrição da natureza jurídica'),
  email: string().label('Email'),
  street: string().label('Logradouro'),
  number: string().label('Número'),
  city: string().label('Cidade'),
  neighborhood: string().label('Bairro'),
  state: string().label('Estado'),
  registrationStatus: enumerated({
    active: 'Ativo',
    inapt: 'Inapto',
    suspended: 'Suspenso',
    extinct: 'Extinto',
  }).label('Status'),
  registrationStatusDate: dateTime().label('Data de situação cadastral'),
  registrationStatusReason: string().label('Motivo do status de registro'),
  size: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),
  segmentId: string().label('Identificador do segmento'),
  segmentName: string().label('Nome do segmento'),
  isSimples: boolean().label('É Simples Nacional'),
  isMei: boolean().label('É MEI'),
  capital: number().label('Capital social'),
  phoneNumber1AreaCode: string().label('DDD do telefone principal'),
  fullAddress: string().label('Endereço'),
  fullPhoneNumber1: string().label('Telefone principal'),
  fullPhoneNumber2: string().label('Telefone secundário'),
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
  }).label('Ponto na rua'),

  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})

export const distinctLegalEntitiesEmissorNfeExpression = expression(combinedShape.shape)

export const distinctLegalEntitiesEmissorNfeVariable: Variable = {
  key: 'distinctLegalEntitiesEmissorNfe',
  categoryKey: 'distinctLegalEntitiesEmissorNfe',
  categoryKeys: ['distinctLegalEntitiesEmissorNfe'],
  actionDefinitionKey: distinctLegalEntitiesEmissorNfeStatsAction.key,
  title: 'Empresas distintas atendidas pelo Emissor NF-e',
  label: 'Geral',
  unitSingular: 'empresa',
  unitPlural: 'empresas',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  valueRules: {
    minValue: 0,
  },
  relevance: 0.5,
  payload: {
    productName: 'Empresas distintas atendidas pelo Emissor NF-e',
    portfolioName: ['Emissor NF-e'],
  },
  basicSelect: ['corporateName', 'emissorDistinctLegalEntity.attendanceDate'],
  exportSortBy: { key: 'emissorDistinctLegalEntity.attendanceDate', direction: 'desc' },
  detailedSelect: [
    'emissorDistinctLegalEntity.cnpj',
    'tradeName',
    'corporateName',
    'registrationStatus',
    'openingDate',
    'mainCnaeId',
    'mainCnaeDescription',
    'segmentId',
    'segmentName',
    'cnaes',
    'legalNatureId',
    'legalNatureDescription',
    'size',
    'sizeAbbreviation',
    'ageGroup',
    'isSimples',
    'isMei',
    'email',
    'street',
    'number',
    'neighborhood',
    'city',
    'state',
    'phoneNumber1AreaCode',
    'fullPhoneNumber1',
    'emissorDistinctLegalEntity.attendanceDate',
  ],
  filterableBy: expression(pick(combinedShape.shape, ['size', 'ageGroup', 'registrationStatus'])),
  timeSeries: eadAttendanceVariable.timeSeries,
  assertions: prepareAssertions(distinctLegalEntitiesEmissorNfeExpression),
}

const distinctLegalEntitiesEmissorNfeStatsDefinition = defineAction({
  ...distinctLegalEntitiesEmissorNfeStatsAction,
  input: object({
    variable,
    highlight: distinctLegalEntitiesEmissorNfeExpression.isOptional(),
    filter: distinctLegalEntitiesEmissorNfeExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default distinctLegalEntitiesEmissorNfeStatsDefinition
